import './ReteachPlate.scss';
import React, {useState, useMemo} from 'react';
import {FmlxButton, FmlxIcon, FmlxTable} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import {selectIsPlateRecognitionEnabled} from 'stores/selectors/plateRecognitionSelectors';
import {HeaderLayout} from '../../components';
import DeepWellLabel from '../../components/DeepWellLabel';
import PageLayout from '../../components/Layout/PageLayout';
import {DefaultWellValue, PlateType, ViewMode, WellCount} from '../../constants/dataEnum';
import withIntl from '../../lib/withIntl';
import {changeView} from '../../stores/actions/appStateAction';
import {restorePlateTypeEditorState} from '../../stores/actions/plateTypeEditorStateAction';
import {showCloseReteachPlateDialog} from '../../stores/business/commonModalBusiness';
import {getAllReteachPlates} from '../../stores/selectors/reteachPlateSelectors';
import {exitReteachPlatePage} from '../../stores/business/reteachPlateBusiness';
import {addTemporaryState} from '../../stores/actions/temporaryStateAction';

const HEADER_STYLE_8 = {width: '8%', maxWidth: '8%', minWidth: '8%'};
const HEADER_STYLE_10 = {width: '10%', maxWidth: '10%', minWidth: '10%'};
const HEADER_STYLE_12 = {width: '12%', maxWidth: '12%', minWidth: '12%'};

function ReteachPlate({intl}) {
  const dispatch = useDispatch();

  const deepWellThreshold = useSelector((state) => state.configState.deepWellThreshold);
  const reteachPlates = useSelector(getAllReteachPlates);
  const isPlateRecognitionEnabled = useSelector(selectIsPlateRecognitionEnabled);

  const [sortValue, setSortValue] = useState({sortType: undefined, sortColumn: undefined});

  const doEditRowHandler = async (labwareDefinition) => {
    const {
      id,
      wellBottomData,
      wellSurfaceData,
      description,
      partNumber,
      vendor,
      notes,
      rowPitch,
      columnPitch,
      wellCapacity,
      workingCapacity,
      firstWellOffset,
      wellNumber,
      type,
      wellLabel,
    } = labwareDefinition;
    const {Shape: wellBottomDataShape, WellDepth: depth} = wellBottomData;
    const {Shape: wellSurfaceDataShape, Width: width, Length: length, Diameter: diameter} = wellSurfaceData;
    const {x, y, z} = firstWellOffset;
    let troughDepth = 0;
    let colPitchAssaySample = 0;

    if (type === PlateType.IFC) {
      if (troughDepth === 0) {
        troughDepth = depth - 0.5;
        switch (wellNumber) {
          case WellCount.WELL_IFC_192_24:
            colPitchAssaySample = DefaultWellValue.WELL_IFC_192_24.columnPitchAssaySample;
            break;
          default:
            break;
        }
      }
    }

    const labwareDefinitionData = {
      id,
      currentWizardView: isPlateRecognitionEnabled ? 4 : 3,
      allowedToNext: true,
      processedStep: 0,
      wellNumber,
      wellLabel,
      wellBottomProfile: wellBottomDataShape || 'Undefined',
      wellShape: wellSurfaceDataShape || 'Undefined',
      plateName: description,
      partNumber,
      vendor,
      notes,
      height: z,
      depth,
      width,
      length: wellSurfaceDataShape === 'Circle' ? diameter : length,
      columnOrigin: y,
      rowOrigin: x,
      columnPitch,
      rowPitch,
      capacity: wellCapacity,
      workingVolume: workingCapacity,
      plateType: type,
      troughDepth,
      colPitchAssaySample,
      isReteachingImportedLabware: true,
    };

    dispatch(restorePlateTypeEditorState(labwareDefinitionData));
    dispatch(addTemporaryState('plateTypeEditorState', labwareDefinitionData));

    dispatch(changeView(ViewMode.PLATE_TYPE_EDITOR));
  };

  const editRowHandler = (e, labwareDefinition) => {
    e.stopPropagation();

    doEditRowHandler(labwareDefinition);
  };

  const generateData = (e) => ({
    _original: e,
    id: e.id,
    plateName: e.description,
    numberOfWell: e.type === PlateType.MICROPLATE ? e.rowCount * e.colCount : e.wellNumber,
    wellBottomProfile: e.wellBottomData.Shape,
    wellShape: e.wellSurfaceData.Shape,
    wellCapacity: e.wellCapacity,
    maxVolume: e.workingCapacity,
    notes: e.notes,
    dateModified: e.lastModifiedTime ? moment(e.lastModifiedTime).toDate() : '',
    actionButtons: (
      <div style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center'}}>
        <FmlxButton
          type="primary"
          onClick={(ee) => {
            editRowHandler(ee, e);
          }}
          withTooltip
          tooltipDescription="Repeat touch-probe calibration process"
          label={intl.formatMessage({id: 'reteachPlate.button'})}
          size="sm"
        />
      </div>
    ),
  });

  const memoizedDataSource = useMemo(() => {
    const tableData = reteachPlates.map((e) => generateData(e));

    const sortedTableData = (() => {
      const {sortColumn, sortType} = sortValue;
      if (sortColumn && sortType) {
        return _.orderBy(tableData, [sortColumn], [sortType]);
      }
      return tableData;
    })();

    return sortedTableData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reteachPlates, sortValue.sortColumn, sortValue.sortType]);

  const handleCloseReteachPlate = () => {
    dispatch(
      showCloseReteachPlateDialog(intl.formatMessage({id: 'modal.showCloseReteachPlate.title'}), () => {
        dispatch(exitReteachPlatePage());
      }),
    );
  };

  const onDoubleClickRow = (e) => {
    doEditRowHandler(e._original);
  };

  const COLUMNS = useMemo(
    () => [
      {
        Header: 'Plate Name',
        accessor: 'plateName',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (data) => {
          const {value, row} = data;
          return (
            <div style={{display: 'flex', alignItems: 'center'}}>
              {value}
              &nbsp;
              {row.original._original.wellBottomData.WellDepth > deepWellThreshold ? <DeepWellLabel /> : ''}
            </div>
          );
        },
        headerStyle: HEADER_STYLE_12,
      },
      {Header: 'Number of Well', accessor: 'numberOfWell', headerStyle: HEADER_STYLE_8},
      {
        Header: 'Well Bottom Profile',
        accessor: 'wellBottomProfile',
        headerStyle: HEADER_STYLE_12,
      },
      {
        Header: 'Well Shape',
        accessor: 'wellShape',
        headerStyle: HEADER_STYLE_8,
      },
      {
        Header: 'Well Capacity',
        accessor: 'wellCapacity',
        headerStyle: HEADER_STYLE_10,
      },
      {Header: 'Max Volume', accessor: 'maxVolume', headerStyle: HEADER_STYLE_8},
      {Header: 'Notes', accessor: 'notes', headerStyle: HEADER_STYLE_12},
      {
        Header: 'Date Modified',
        accessor: 'dateModified',
        hideFilter: true, // quick fix for exception if filter type is date
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (data) => {
          const {value} = data;
          return <>{moment(value).format('YYYY-MM-DD')}</>;
        },
        headerStyle: HEADER_STYLE_12,
      },
      {
        Header: 'Action',
        accessor: 'actionButtons',
        disableSort: true,
        hideSort: true,
        hideFilter: true,
      },
    ],
    [deepWellThreshold],
  );

  const handleChangeSort = (e) => {
    const {type, column} = e;
    if (!column || !type) return;
    setSortValue({sortType: type, sortColumn: column});
  };

  return (
    <PageLayout>
      <PageLayout.Header>
        <HeaderLayout>
          <HeaderLayout.Left>
            <h1 className="reteach-title">{intl.formatMessage({id: 'reteachPlate.title'})}</h1>
          </HeaderLayout.Left>
          <HeaderLayout.Right>
            <button className="reteach-cancel-button" type="button" onClick={handleCloseReteachPlate}>
              <FmlxIcon name="CloseOutline" customColor="white" />
            </button>
          </HeaderLayout.Right>
        </HeaderLayout>
      </PageLayout.Header>
      <PageLayout.Content>
        <div className="reteach-plate-section">
          <FmlxTable
            data={memoizedDataSource}
            columns={COLUMNS}
            getSortValue={handleChangeSort}
            placeholder={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img src="./svg/labware-manager/plate-not-found.svg" alt="plate not found icon" />
                <div
                  style={{
                    fontSize: 24,
                    color: '#707070',
                    marginTop: 40,
                  }}
                >
                  Plate Not Found
                </div>
              </div>
            }
            onRowDoubleClick={onDoubleClickRow}
            showToolbar={false}
          />
        </div>
      </PageLayout.Content>
    </PageLayout>
  );
}

ReteachPlate.propTypes = {
  intl: PropTypes.any.isRequired,
};

ReteachPlate.defaultProps = {};

export default withIntl(ReteachPlate);
